import { Injectable } from '@angular/core';

import { AzavistaApiService, IParticipantAudit, IStage, IUser, ParticipantAuditUserType } from '@azavista/servicelib';
import { AzavistaSharedService, CustomTableActionType, IField, InputFieldType } from '@azavista/components/shared';
import { AzavistaInputFieldService } from '@azavista/components/input-field';
import { IColumnsListSetting, IconName, SettingEntityType, SettingParentType, SettingsService, SharedService, TRANSLATION_MAP, TranslationKey, TranslationMapKey } from 'src/app/shared';
import { ICustomTableAction } from '@azavista/components/table';
import { AuditCustomTableActionId } from './enums';
import { IAuditCardData } from './interfaces';

const AUDIT_LOG_FIELD_LABEL: { [field in keyof IParticipantAudit]?: TranslationMapKey } = {
    user_type: TRANSLATION_MAP.USER_TYPE,
};

export const AUDIT_LOG_USER_TYPE_LABEL: { [field in ParticipantAuditUserType]: TranslationMapKey } = {
    [ParticipantAuditUserType.participant]: TRANSLATION_MAP.PARTICIPANT,
    [ParticipantAuditUserType.workflow]: TRANSLATION_MAP.WORKFLOW,
    [ParticipantAuditUserType.user]: TRANSLATION_MAP.USER,
};

@Injectable({
    providedIn: 'root'
})
export class AuditTabService {

    private readonly fieldNames = {
        createdAt: 'created_at',
        crmStatus: 'crmstatus',
        stageId: 'stage_id',
        __userDisplayText: '__userDisplayText',
        firstName: 'first_name',
        userType: 'user_type',
        lastName: 'last_name'
    };
    constructor(
        private sharedSvc: SharedService, private apiSvc: AzavistaApiService, private settingsSvc: SettingsService,
        private cmdSharedSvc: AzavistaSharedService, private inputFldSvc: AzavistaInputFieldService
    ) {
    }

    getUserDisplayText(user: IUser, defaultValue: string): string {
        if (!user) {
            return defaultValue;
        }
        let userDisplayText = (user.first_name || '') + ' ' + (user.last_name || '');
        if (!userDisplayText || !userDisplayText.trim()) {
            userDisplayText = user.email || defaultValue;
        }
        return userDisplayText;
    }

    createCardsData(data: IParticipantAudit[], allColumns: IField[], allUsers: IUser[]): IAuditCardData[] {
        const result: IAuditCardData[] = [];
        const filteredFieldNames = [
            'id', 'event_id', 'participant_id', 'customer_id', 'user_id',
            this.fieldNames.createdAt, this.fieldNames.__userDisplayText
        ];
        const currentUser = this.apiSvc.getCurrentUser();
        for (const audit of data) {
            const auditAny = audit as any;
            const dateTimeText = this.inputFldSvc.getFormattedValue(
                auditAny[this.fieldNames.createdAt], null, 'datetime', currentUser.language, currentUser.datetime_format, '', '', null, null
            );
            const auditUser = allUsers.find(x => x.id === audit.user_id);
            const userDisplayText = this.getUserDisplayText(auditUser,  `${audit.user_id ?? ''}`);
            const cardData: IAuditCardData = {
                id: audit.id,
                createdAt: '' + dateTimeText,
                user: userDisplayText,
                items: []
            };
            const auditKeys = Object.keys(audit).filter(x => !filteredFieldNames.includes(x));
            for (const key of auditKeys) {
                const column = allColumns.find(x => x.name === key);
                const label = this.getCardLabel(column, key);
                const value = key === 'user_type' && audit[key]
                    ? this.sharedSvc.translate(AUDIT_LOG_USER_TYPE_LABEL[audit[key]])
                    : auditAny[key];
                cardData.items.push({ label, value });
            }
            this.sharedSvc.sortAlphabetically(cardData.items, x => x.label);
            result.push(cardData);
        }
        return result;
    }

    private getCardLabel(column: IField, key: string) {
        const predefinedLabel = AUDIT_LOG_FIELD_LABEL[key as keyof IParticipantAudit];
        return column?.label
            || (predefinedLabel && this.sharedSvc.translate(predefinedLabel)) || key;
    }

    getCustomTableActions(): ICustomTableAction[] {
        const result: ICustomTableAction[] = [
            {
                id: AuditCustomTableActionId.selectUser, type: CustomTableActionType.textButton,
                text: this.sharedSvc.translate(TranslationKey.selectUser)
            },
            // the default selected CustomTableActionType.button should be put first of the CustomTableActionType.button so it get highlighted
            { id: AuditCustomTableActionId.card, icon: IconName.view_module, type: CustomTableActionType.button },
            { id: AuditCustomTableActionId.table, icon: IconName.view_list, type: CustomTableActionType.button },
        ] as ICustomTableAction[];
        return result;
    }

    getFieldsForAdvancedSearch(allParticipantFields: IField[], allStages: IStage[]): IField[] {
        const result = this.getSupportedFieldTypesForAdvancedSearch(allParticipantFields);
        // Add stage
        this.sharedSvc.addParticipantStageIdField(result, allStages);
        this.sharedSvc.sortAlphabetically(result, (item) => item.label);
        return result;
    }

    async setUserDisplayText(participantAuits: IParticipantAudit[], users: IUser[]): Promise<void> {
        for (const audit of participantAuits) {
            const auditAny = audit as any;
            const user = users.find(x => x.id === audit.user_id);
            auditAny[this.fieldNames.__userDisplayText] = this.getUserDisplayText(user, `${audit.user_id ?? ''}`);
        }
    }

    setStageNames(participantAuits: IParticipantAudit[], allEventStages: IStage[]): void {
        for (const audit of participantAuits) {
            const auditAny = audit as any;
            const stageName = allEventStages.find(x => x.id === audit.stage_id)?.name;
            auditAny[this.fieldNames.stageId] = stageName || audit.stage_id;
        }
    }

    getCreatedAtField(fields: IField[]): IField {
        return fields.find(x => x.name === this.fieldNames.createdAt);
    }

    getColumns(allParticipantFields: IField[]): IField[] {
        const result: IField[] = [];
        // Add artificial fields like __stage_name, __user_name
        // and also important fields of the response itself like created_at
        result.push({
            id: this.fieldNames.createdAt, name: this.fieldNames.createdAt,
            label: this.sharedSvc.translate(TranslationKey.createdAt),
            schema: { type: 'string', format: 'date-time' }
        } as IField);
        result.push({
            id: this.fieldNames.__userDisplayText, name: this.fieldNames.__userDisplayText,
            label: this.sharedSvc.translate(TranslationKey.user),
            schema: { type: 'string' }
        } as IField);
        result.push({
            id: this.fieldNames.stageId, name: this.fieldNames.stageId,
            label: this.sharedSvc.translate(TranslationKey.stage),
            schema: { type: 'string' }
        } as IField);
        result.push({
            id: this.fieldNames.crmStatus, name: this.fieldNames.crmStatus,
            label: this.sharedSvc.translate(TranslationKey.crmStatus),
            schema: { type: 'string' }
        } as IField);
        result.push(this.sharedSvc.createSelectField({
            id: this.fieldNames.userType,
            name: this.fieldNames.userType,
            label: this.sharedSvc.translate(TRANSLATION_MAP.USER_TYPE),
        }, AUDIT_LOG_USER_TYPE_LABEL));
        const supportedFields = this.getSupportedFieldTypesForAdvancedSearch(allParticipantFields);
        result.push(...supportedFields);
        return result;
    }

    getSupportedFieldTypesForAdvancedSearch(fields: IField[]): IField[] {
        const notSupportedFieldTypes: InputFieldType[] = [
            'document', 'image', 'unknown', 'virtual-relation'
        ];
        const filteredFields = fields.filter(x => !notSupportedFieldTypes.includes(this.cmdSharedSvc.getFieldTypeFromField(x)));
        return filteredFields;
    }

    async getColumnsSettings(allParticipantFields: IField[], eventId: string): Promise<IColumnsListSetting> {
        const allColumns = this.getColumns(allParticipantFields);
        const allowedIds = allColumns.map(x => x.id);
        const defaultIds = [
            this.fieldNames.createdAt, this.fieldNames.__userDisplayText,
            this.fieldNames.stageId, this.fieldNames.crmStatus
        ];
        const columnsSettings = await this.settingsSvc.getParentColumnsOrder(SettingParentType.event, eventId, SettingEntityType.participantAudit, allowedIds, defaultIds);
        return columnsSettings;
    }
}
